import { useEffect, useRef, useState } from "react";
import { Prompt } from "../components/Audio";
import Feedback from "../components/Feedback";
import { CheckIcon, CrossIcon } from "../components/Icons";
import ItemCard from "../components/ItemCard";
import Picture from "../components/Picture";
import ui from "../components/ui.module.css";
import { isChoiceRight } from "../data/activityAdapter";

const CELEBRATE_MS = 700;
const HINT_AFTER = 2;

// Yes and No differ by shape as well as by word, so neither colour nor
// reading is needed to tell them apart.
const MARKS = {
  yes: <CheckIcon className={ui.markYes} size={88} />,
  no: <CrossIcon className={ui.markNo} size={88} />,
};

// Look at the picture, then choose. A wrong tap gets a gentle "try again" and is not saved;
// only the resulting choice is (handoff §12).
const ChoiceStep = ({ step, onDone, saving }) => {
  const [picked, setPicked] = useState(null);
  const [wrong, setWrong] = useState(null);
  const [misses, setMisses] = useState(0);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const choose = (option) => {
    if (picked || saving) return;
    if (!isChoiceRight(step, option.value)) {
      setWrong(option.value);
      setMisses((n) => n + 1);
      return;
    }
    setWrong(null);
    setPicked(option.value);
    timer.current = setTimeout(
      () => onDone({ value: option.value }),
      CELEBRATE_MS
    );
  };

  const hint = misses >= HINT_AFTER && !picked ? step.correct : null;

  return (
    <>
      {step.stimulus && (
        <img
          className={ui.stimulus}
          src={step.stimulus.src}
          alt={step.stimulus.alt}
        />
      )}
      <Prompt step={step} />
      <div className={ui.choices}>
        {step.options.map((option) => (
          <ItemCard
            // Re-mounting on each miss replays the shake.
            key={option.value === wrong ? `${option.value}-${misses}` : option.value}
            item={option}
            className={[
              ui.choice,
              picked === option.value && ui.choiceRight,
              wrong === option.value && ui.shake,
              hint === option.value && ui.choiceHint,
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={() => choose(option)}
            aria-pressed={picked === option.value}
          >
            {!option.asset && !option.emoji && MARKS[option.value]}
            <Picture item={option} />
          </ItemCard>
        ))}
      </div>
      <Feedback kind={picked ? "right" : wrong ? "wrong" : null} />
    </>
  );
};

export default ChoiceStep;
